import type { ThemeColorProfile } from '@commonqr/shared/config/themeEngine';

function linearise(c: number): number {
  const s = c / 255;
  return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

function luminanceFromRGB(r: number, g: number, b: number): number {
  return 0.2126 * linearise(r) + 0.7152 * linearise(g) + 0.0722 * linearise(b);
}

function contrastRatioFromLum(l1: number, l2: number): number {
  const lighter = Math.max(l1, l2);
  const darker  = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

function wcagTextColor(bgLum: number): string {
  const onWhite = contrastRatioFromLum(1.0, bgLum);
  const onBlack = contrastRatioFromLum(0.0, bgLum);
  return onWhite >= onBlack ? '#ffffff' : '#000000';
}

function toHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
}

interface ZoneSample {
  luminance: number;
  dominant: [number, number, number];
}

function sampleZone(data: Uint8ClampedArray, width: number, yStart: number, yEnd: number, step: number): ZoneSample {
  let lumSum = 0;
  let count  = 0;
  let bestSat = -1;
  let dominant: [number, number, number] = [128, 128, 128];
  for (let y = yStart; y < yEnd; y += step) {
    for (let x = 0; x < width; x += step) {
      const idx = (y * width + x) * 4;
      const r = data[idx];
      const g = data[idx + 1];
      const b = data[idx + 2];
      lumSum += luminanceFromRGB(r, g, b);
      count++;
      const max = Math.max(r, g, b) / 255;
      const min = Math.min(r, g, b) / 255;
      const sat = max === 0 ? 0 : (max - min) / max;
      if (sat > bestSat) { bestSat = sat; dominant = [r, g, b]; }
    }
  }
  return { luminance: count > 0 ? lumSum / count : 0.5, dominant };
}

function requiredOverlayOpacity(bgLum: number): number {
  if (bgLum <= 0.175) return 0;
  const opacity = 1 - 0.175 / bgLum;
  return Math.min(Math.max(opacity, 0), 0.75);
}

export interface AnalyzerOptions {
  step?: number;
  sampleWidth?: number;
  sampleHeight?: number;
}

export async function analyzeBackground(imageUrl: string, options: AnalyzerOptions = {}): Promise<ThemeColorProfile> {
  const { step = 10, sampleWidth = 100, sampleHeight = 200 } = options;
  const img = await loadImage(imageUrl);
  const canvas = document.createElement('canvas');
  canvas.width  = sampleWidth;
  canvas.height = sampleHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('backgroundAnalyzer: Canvas 2D context unavailable');
  ctx.drawImage(img, 0, 0, sampleWidth, sampleHeight);
  const { data } = ctx.getImageData(0, 0, sampleWidth, sampleHeight);
  const h3 = Math.floor(sampleHeight / 3);
  const top    = sampleZone(data, sampleWidth, 0,      h3,           step);
  const mid    = sampleZone(data, sampleWidth, h3,     h3 * 2,       step);
  const bottom = sampleZone(data, sampleWidth, h3 * 2, sampleHeight, step);
  const overallLum = top.luminance * 0.25 + mid.luminance * 0.5 + bottom.luminance * 0.25;
  const textColor = wcagTextColor(overallLum);
  const midSat = saturation(mid.dominant);
  const accent = midSat > 0.15 ? mid.dominant : saturation(top.dominant) > 0.15 ? top.dominant : bottom.dominant;
  const buttonColor = toHex(...accent);
  const overlayOpacity = requiredOverlayOpacity(overallLum);
  const textLum        = textColor === '#ffffff' ? 1.0 : 0.0;
  const effectiveBgLum = overallLum * (1 - overlayOpacity);
  const finalContrast  = contrastRatioFromLum(textLum, effectiveBgLum);
  return {
    textColor, buttonColor, overlayOpacity,
    luminanceTop: top.luminance, luminanceMid: mid.luminance, luminanceBottom: bottom.luminance,
    contrastRatio: Math.round(finalContrast * 100) / 100,
  };
}

export function profileFromDB(dominantColor: string, luminance: number, recommendedTextColor: '#ffffff' | '#000000'): ThemeColorProfile {
  const textLum        = recommendedTextColor === '#ffffff' ? 1.0 : 0.0;
  const overlayOpacity = requiredOverlayOpacity(luminance);
  const effectiveLum   = luminance * (1 - overlayOpacity);
  const contrastRatio  = Math.round(contrastRatioFromLum(textLum, effectiveLum) * 100) / 100;
  return { textColor: recommendedTextColor, buttonColor: dominantColor, overlayOpacity,
    luminanceTop: luminance, luminanceMid: luminance, luminanceBottom: luminance, contrastRatio };
}

function saturation([r, g, b]: [number, number, number]): number {
  const max = Math.max(r, g, b) / 255;
  const min = Math.min(r, g, b) / 255;
  return max === 0 ? 0 : (max - min) / max;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload  = () => resolve(img);
    img.onerror = () => reject(new Error('backgroundAnalyzer: failed to load ' + src));
    img.src = src;
  });
}